import React, { useCallback, useMemo, useState } from 'react';

import { DownOutlined, ExportOutlined, FilterOutlined, UpOutlined } from '@ant-design/icons';
import { Badge, Button, Col, DatePicker, Row, Tabs } from 'antd';

import clsx from 'clsx';
import Alert from 'components/Alert';
import FilterTags from 'components/FilterTags';
import Item from 'components/FilterTags/Item';
import differenceInMonths from 'date-fns/differenceInMonths';
import endOfDay from 'date-fns/endOfDay';
import isAfter from 'date-fns/isAfter';
import parse from 'date-fns/parse';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import subDays from 'date-fns/subDays';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { URL_DOCS_FISCAIS } from 'settings';
import Banner from 'v2/components/Shared/Banner';
import documentMask from 'v2/components/Shared/Masks/document';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import TopPage from 'v2/components/Shared/TopPage';
import type { IFilterParams } from 'v2/services/cdfService/types';
import { useFilterTagStore } from 'v2/store/FilterTag';

import { DateLimitAlert } from './components/DateLimitAlert';
import ModalReport from './components/ModalReport';
import QuickView from './components/QuickView';
import Table from './components/Table';
import { FiscalDocumentsProvider } from './context';

const TABS = ['send_producer', 'received_affiliates', 'customers', 'received_eduzz'];

const EDUZZ_SEARCH_LIMIT = parse('2023/03/31', 'yyyy/MM/dd', new Date());

const FILTER_STATUS = [
  { value: 1, label: 'Novo' },
  { value: 2, label: 'Pendente' },
  { value: 3, label: 'Aguard. Dcto' },
  { value: 4, label: 'Cancelado' },
  { value: 5, label: 'Processado' },
  { value: 6, label: 'Aprovado' },
  { value: 7, label: 'Arquivo Gerado' },
  { value: 8, label: 'Em Processamento' }
];

interface FilterItem {
  value: string | number;
  label: string;
  applied?: boolean;
}

function getAppliedValues(items: FilterItem[], transform?: (value: string | number) => string | number): string[] {
  return (items || []).filter(s => s.applied).map(it => String(transform ? transform(it.value) : it.value));
}

function getCustomersTabInitialDateFilter(currentDate: Date | undefined): Date {
  const today = new Date();

  if (currentDate && isAfter(startOfDay(currentDate), EDUZZ_SEARCH_LIMIT)) {
    return startOfMonth(EDUZZ_SEARCH_LIMIT);
  }

  if (!isAfter(startOfDay(today), startOfDay(EDUZZ_SEARCH_LIMIT))) {
    return currentDate ?? startOfDay(subDays(new Date(), 7));
  }

  return startOfMonth(EDUZZ_SEARCH_LIMIT);
}

function getCustomersTabFinalDateFilter(currentDate: Date | undefined): Date {
  const today = new Date();

  if (currentDate && isAfter(startOfDay(currentDate), EDUZZ_SEARCH_LIMIT)) {
    return endOfDay(EDUZZ_SEARCH_LIMIT);
  }

  if (!isAfter(startOfDay(today), startOfDay(EDUZZ_SEARCH_LIMIT))) {
    return currentDate ?? endOfDay(new Date());
  }

  return endOfDay(EDUZZ_SEARCH_LIMIT);
}

const disabledDateForCustomerDocumentsTab = (current: Dayjs) => current.isAfter(dayjs(EDUZZ_SEARCH_LIMIT), 'day');
const disabledDateNoop = () => false;

const onFormatDocument = (value: string | number) => documentMask.apply(value);

const FiscalDocuments: React.FC = () => {
  const { filter } = useFilterTagStore();

  const [tab, setTab] = useState('0');
  const [filterActive, setFilterActive] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [baseFilterParams, setBaseFilterParams] = useState({
    type: 'send_producer',
    received_at: startOfDay(subDays(new Date(), 7)),
    received_at_final: endOfDay(new Date())
  });

  const filterParams = useMemo<IFilterParams>(
    () => ({
      ...baseFilterParams,
      origin_customer_name: getAppliedValues(filter['Nome (Origem)']),
      origin_customer_email: getAppliedValues(filter['E-mail (Origem)']),
      origin_document_number: getAppliedValues(filter['CNPJ/CPF (Origem)'], documentMask.clean),
      destination_customer_name: getAppliedValues(filter['Nome (Destino)']),
      destination_customer_email: getAppliedValues(filter['E-mail (Destino)']),
      destination_document_number: getAppliedValues(filter['CNPJ/CPF (Destino)'], documentMask.clean),
      invoice_id: getAppliedValues(filter['Fatura']),
      doc_status: getAppliedValues(filter['Status'])
    }),
    [baseFilterParams, filter]
  );

  const onChangeDate = useCallback((dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates) return;
    const [start, end] = dates;

    if (start && end) {
      setBaseFilterParams(prev => ({
        ...prev,
        received_at: start.toDate(),
        received_at_final: end.toDate()
      }));
    }
  }, []);

  const toggleFilter = useCallback(() => {
    setFilterActive(prev => !prev);
  }, []);

  const onTabChange = useCallback(
    (newTab: string) => {
      const tabIndex = parseInt(newTab);
      let initialFilterDate = baseFilterParams.received_at;
      let finalFilterDate = baseFilterParams.received_at_final;

      if (tabIndex === 2) {
        initialFilterDate = getCustomersTabInitialDateFilter(baseFilterParams.received_at);
        finalFilterDate = getCustomersTabFinalDateFilter(baseFilterParams.received_at_final);
      }

      setTab(newTab);
      setBaseFilterParams(prev => ({
        ...prev,
        received_at: initialFilterDate,
        received_at_final: finalFilterDate,
        type: TABS[tabIndex]
      }));
    },
    [baseFilterParams.received_at, baseFilterParams.received_at_final]
  );

  const onClickExportCDF = useCallback(async () => {
    const { received_at, received_at_final } = baseFilterParams;
    const monthsDiff = differenceInMonths(received_at_final, received_at);

    if (monthsDiff > 6) {
      await Alert.show({
        title: 'Este relatório é muito grande =/',
        message: `
          Os robôzinhos do nosso sistema ainda não conseguem exportar relatórios com mais de 6 meses.
          Estamos treinando eles para que isso seja possível em breve.
          Experimente pesquisas com resultados menores e dará tudo certo ;)
        `
      });
      return;
    }

    setIsExporting(true);
  }, [baseFilterParams]);

  const onCloseExportCDF = useCallback(() => {
    setIsExporting(false);
  }, []);

  return (
    <div className='animate-fadeIn'>
      <TopPage
        title='Central de Documentos Fiscais'
        extra='Acompanhe todos dados necessários para emissão e conferência de Recibos e Notas Fiscais de seu Negócio.'
        additionalContent={
          <>
            <PermissionHide action='myeduzz:cdf:exportar'>
              <Button type='primary' onClick={onClickExportCDF} loading={isExporting} icon={<ExportOutlined />}>
                {isExporting ? 'Exportando...' : 'Exportar'}
              </Button>
            </PermissionHide>

            <a href={URL_DOCS_FISCAIS} target='_blank' rel='noopener noreferrer'>
              <Button>Experimente o novo split</Button>
            </a>
          </>
        }
      />

      <Banner.Module module='myeduzz-fiscal-document-banner' />

      <Tabs
        activeKey={tab}
        onChange={onTabChange}
        items={[
          { key: '0', label: 'Enviado Produtores' },
          { key: '1', label: 'Recebido Afiliados' },
          { key: '2', label: 'Notas a Clientes' },
          { key: '3', label: 'Recebidos Eduzz' }
        ]}
      />

      {tab === '2' && (
        <div className='mb-4'>
          <DateLimitAlert />
        </div>
      )}

      <Row justify='end' gutter={[16, 16]}>
        <Col xs={24} md={10} lg={8} xl={6}>
          <DatePicker.RangePicker
            className='w-full'
            value={[dayjs(filterParams.received_at), dayjs(filterParams.received_at_final)]}
            onChange={onChangeDate}
            format='DD/MM/YYYY'
            placeholder={['Data inicial', 'Data final']}
            disabledDate={tab === '2' ? disabledDateForCustomerDocumentsTab : disabledDateNoop}
            allowClear={false}
          />
        </Col>
        <Col xs={24} md={4} lg={3}>
          <div className={clsx('rounded-tl-md rounded-tr-md', { 'bg-grey-200': filterActive })}>
            <Button type='text' block onClick={toggleFilter} icon={<FilterOutlined />}>
              <Badge count={filter ? filter.count : 0} size='small'>
                Filtrar
              </Badge>
              {filterActive ? <UpOutlined className='ml-2!' /> : <DownOutlined className='ml-2!' />}
            </Button>
          </div>
        </Col>
      </Row>

      <FilterTags onApply={() => false} active={filterActive}>
        <Item name='Nome (Origem)' type='multiple-text' tagLabel='Nome (Origem)' />
        <Item name='E-mail (Origem)' type='multiple-text' tagLabel='E-mail (Origem)' />
        <Item name='CNPJ/CPF (Origem)' type='multiple-text' tagLabel='CNPJ/CPF (Origem)' format={onFormatDocument} />
        <Item name='Nome (Destino)' type='multiple-text' tagLabel='Nome (Destino)' />
        <Item name='E-mail (Destino)' type='multiple-text' tagLabel='E-mail (Destino)' />
        <Item name='CNPJ/CPF (Destino)' type='multiple-text' tagLabel='CNPJ/CPF (Destino)' format={onFormatDocument} />
        <Item name='Fatura' type='multiple-text' tagLabel='Fatura' />
        <Item name='Status' type='multiple' tagLabel='Status' options={FILTER_STATUS} label='label' value='value' />
      </FilterTags>

      <FiscalDocumentsProvider>
        <Table params={filterParams} />
        <QuickView />
        <ModalReport open={isExporting} onClose={onCloseExportCDF} filterParams={filterParams} />
      </FiscalDocumentsProvider>
    </div>
  );
};

export default FiscalDocuments;
